/**
 *
 * Footer
 *
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  showAbout,
  showPrivacyPolicy,
  showTermsOfUse,
} from 'containers/Main/actions';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import ClassNames from 'classnames';
import {
  makeSelectClientDetails,
  makeSelectFooter,
} from 'containers/Main/selectors';
import { makeStyles } from '@material-ui/core/styles';
import Colors from 'utils/colors';
import Grid from '@material-ui/core/Grid';
import { replacePlaceholders } from 'utils/stringUtils';
import { isEmbedded } from 'utils/embedded';
import Typography from '@material-ui/core/Typography';
import RichTextReactRenderer from 'components/RichTextReactRenderer';
import useSiteCopySelector from 'components/useSiteCopySelector';
import ButtonBase from '@material-ui/core/ButtonBase';

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: 176,
    position: 'relative',
    bottom: 0,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 30,
    [theme.breakpoints.down('400')]: {
      marginTop: 50,
    },
  },
  isEmbed: {
    background: '#F8F9F9',
  },
  wrapper: {
    maxWidth: 800,
  },
  textWrapper: {
    marginLeft: 10,
    marginRight: 10,
    fontSize: 12,
    '& > div > p': {
      textAlign: 'center !important',
      color: theme.palette.text.primary,
      ...theme.typography.pxSmall,
    },
  },
  link: {
    cursor: 'pointer',
    textDecoration: 'none',
    '&:hover': {
      color: Colors.blue,
      '& > h6': {
        color: Colors.blue,
      },
      '& > p': {
        color: Colors.blue,
      },
    },
  },
  copyright: {
    color: '#505050',
    fontSize: 12,
    lineHeight: 1.5,
    marginTop: 20,
    marginBottom: 40,
  },
  embedCopyright: {
    marginBottom: 0,
    marginTop: 0,
    fontSize: 14,
  },
  menu: {
    justifyContent: 'center',
    display: 'flex',
  },
  menuWrapper: {
    flexDirection: 'row',
    marginTop: 20,
    width: '70%',
    [theme.breakpoints.down('600')]: {
      flexDirection: 'column',
    },
  },
  topInfo: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#A6A6A6',
  },
  btnLink: {
    cursor: 'pointer',
    fontSize: 14,
    lineHeight: 1.5,
    color: theme.palette.primary.main,
    marginLeft: 3,
  },
  mt0: {
    '& p': {
      margin: 0,
    },
  },
}));

function Footer() {
  const classes = useStyles();
  const footer = useSelector(makeSelectFooter());
  const clientDetails = useSelector(makeSelectClientDetails());
  const dispatch = useDispatch();
  const [footerSiteCopy] = useSiteCopySelector(['footer']);
  const isCompanyPage = window.location.pathname.includes('/company/');
  const hasFooterData = !_isEmpty(_get(footer, 'body'));
  const hideAboutAndFeedback = _get(
    clientDetails,
    'metadata.hideNavBarSubMenu',
    false,
  );
  const [hideAboutLink, setHideAboutLink] = useState(
    _get(clientDetails, 'metadata.hideAboutLink', false),
  );

  useEffect(() => {
    if (!_isEmpty(clientDetails))
      setHideAboutLink(_get(clientDetails, 'metadata.hideAboutLink', false));
  }, [clientDetails]);

  const footerData = _get(clientDetails, 'footer.json', footer.body.json);

  const useAppEmbedStyle =
    isEmbedded() || _get(clientDetails, 'metadata.useAppEmbedStyle', false);

  const copyright = (
    <Typography
      align="center"
      gutterBottom
      className={ClassNames(classes.copyright, {
        [classes.embedCopyright]: useAppEmbedStyle,
      })}
    >
      {replacePlaceholders(_get(footerSiteCopy, 'pageCopy.copyrightSentence'), {
        '#currentYear': new Date().getFullYear(),
      })}
    </Typography>
  );

  const showAboutLink =
    !hideAboutLink && !hideAboutAndFeedback && !isCompanyPage;

  return (
    <footer
      className={ClassNames(classes.root, {
        [classes.isEmbed]: useAppEmbedStyle,
      })}
    >
      <Grid container className={classes.wrapper}>
        {useAppEmbedStyle && (
          <Grid item xs={12} className={classes.topInfo}>
            {copyright}
            <ButtonBase
              disableRipple
              onClick={() => dispatch(showPrivacyPolicy(true))}
              className={classes.btnLink}
            >
              {_get(footerSiteCopy, 'pageCopy.privacyPolicyLabel')}&nbsp;
            </ButtonBase>
            /
            <ButtonBase
              disableRipple
              onClick={() => dispatch(showTermsOfUse(true))}
              className={classes.btnLink}
            >
              {_get(footerSiteCopy, 'pageCopy.termsOfUseLabel')}
            </ButtonBase>
          </Grid>
        )}
        {!isCompanyPage && hasFooterData && (
          <Grid
            item
            xs={12}
            className={ClassNames(classes.textWrapper, {
              [classes.mt0]: useAppEmbedStyle,
            })}
          >
            <RichTextReactRenderer data={footerData} />
          </Grid>
        )}
        {!useAppEmbedStyle && (
          <>
            <Grid item xs={12} className={classes.menu}>
              <Grid
                container
                direction="row"
                justifyContent="space-around"
                alignItems="center"
                className={classes.menuWrapper}
              >
                {showAboutLink && (
                  <Grid item>
                    <ButtonBase
                      disableRipple
                      onClick={() => dispatch(showAbout(true))}
                    >
                      <Typography
                        variant="body2"
                        className={classes.link}
                        align="center"
                      >
                        {_get(footerSiteCopy, 'pageCopy.aboutLabel')}
                      </Typography>
                    </ButtonBase>
                  </Grid>
                )}
                <Grid item>
                  <ButtonBase
                    disableRipple
                    onClick={() => dispatch(showPrivacyPolicy(true))}
                  >
                    <Typography
                      variant="body2"
                      className={classes.link}
                      align="center"
                    >
                      {_get(footerSiteCopy, 'pageCopy.privacyPolicyLabel')}
                    </Typography>
                  </ButtonBase>
                </Grid>
                <Grid item>
                  <ButtonBase onClick={() => dispatch(showTermsOfUse(true))}>
                    <Typography
                      variant="body2"
                      className={classes.link}
                      color="textPrimary"
                      align="center"
                    >
                      {_get(footerSiteCopy, 'pageCopy.termsOfUseLabel')}
                    </Typography>
                  </ButtonBase>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              {copyright}
            </Grid>
          </>
        )}
      </Grid>
    </footer>
  );
}

export default Footer;
